import { Instagram } from "lucide-react";
import { Wordmark, INSTAGRAM_URL, scrollToMenu } from "@/components/brand/BrandAssets";
import { useSchedule, groupWeeklyHours } from "@/lib/schedule";
import { openCookieSettings } from "@/lib/ads";

const T = {
  meet: { lv: "Kontakti", ru: "Контакты", en: "Contact" },
  hours: { lv: "Darba laiks", ru: "Часы работы", en: "Opening hours" },
  info: { lv: "Informācija", ru: "Информация", en: "Information" },
  menu: { lv: "Ēdienkarte", ru: "Меню", en: "Menu" },
  allergens: { lv: "Alergēni", ru: "Аллергены", en: "Allergens" },
  terms: { lv: "Noteikumi un nosacījumi", ru: "Условия использования", en: "Terms & Conditions" },
  privacy: { lv: "Privātuma politika", ru: "Политика конфиденциальности", en: "Privacy Policy" },
  cookies: { lv: "Sīkdatņu iestatījumi", ru: "Настройки cookies", en: "Cookie settings" },
  company: { lv: "Informācija par uzņēmumu", ru: "О компании", en: "Company info" },
  admin: { lv: "Administrācija", ru: "Администрирование", en: "Admin" },
  rights: { lv: "Visas tiesības aizsargātas.", ru: "Все права защищены.", en: "All rights reserved." },
  tagline: { lv: "Neapoles pica · Piegāde un līdzņemšana · Liepāja", ru: "Неаполитанская пицца · Доставка и самовывоз · Лиепая", en: "Neapolitan pizza · Delivery & pickup · Liepāja" },
};

const Footer = ({ language }) => {
  const t = (k) => T[k][language] || T[k].lv;
  const schedule = useSchedule();
  const colTitle = "rz-eyebrow !text-[#A8A29E] m-0 mb-6";
  const line = "text-[#D6D3D1] text-[0.95rem] leading-[1.9] m-0";
  const link = `${line} rz-link inline-flex items-center min-h-[44px]`;

  return (
    <footer className="bg-[#0C0A09] text-[#D6D3D1] pt-20 sm:pt-28 pb-28 sm:pb-12 px-5 sm:px-8 font-body" data-testid="footer">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-4">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="inline-block" aria-label="Roze di Napoli">
              <Wordmark size="footer" tone="light" />
            </a>
            <p className="m-0 mt-6 text-[0.72rem] tracking-[0.2em] uppercase text-[#78716C] max-w-[18rem] leading-[1.9]">{t("tagline")}</p>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6">
            <div data-testid="footer-contact">
              <h3 className={colTitle}>{t("meet")}</h3>
              <p className={line} data-testid="footer-address">Liepu iela 9<br />Liepāja, LV-3401</p>
              <p className={line}><a href="tel:+37128717331" className={link} data-testid="footer-phone">+371 28717331</a></p>
              <p className={line}><a href="mailto:rozedinapoliorders@gmail.com" className={`${link} break-all`} data-testid="footer-email">rozedinapoliorders@gmail.com</a></p>
            </div>

            <div data-testid="footer-hours">
              <h3 className={colTitle}>{t("hours")}</h3>
              {groupWeeklyHours(schedule.weekly_hours, language).map((g) => (
                <p key={g.label} className={line} data-testid="footer-hours-row">{g.label}<br /><span className="text-[#A8A29E]">{g.value}</span></p>
              ))}
            </div>

            <div data-testid="footer-social">
              <h3 className={colTitle}>{t("info")}</h3>
              <ul className="list-none p-0 m-0 -mt-2">
                <li><a href="#menu-section" onClick={(e) => { e.preventDefault(); scrollToMenu(); }} className={link}>{t("menu")}</a></li>
                <li><a href="/allergens" className={link}>{t("allergens")}</a></li>
                <li>
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className={`${link} gap-2`} data-testid="social-instagram">
                    <Instagram className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> Instagram
                  </a>
                </li>
                <li><a href="/company-info" className={link}>{t("company")}</a></li>
                <li><a href="/terms" className={link}>{t("terms")}</a></li>
                <li><a href="/privacy" className={link}>{t("privacy")}</a></li>
                <li><button type="button" onClick={openCookieSettings} className={link} data-testid="footer-cookie-settings-btn">{t("cookies")}</button></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-6 border-t border-[#292524] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[0.68rem] tracking-[0.18em] uppercase text-[#78716C]">
          <p className="m-0" data-testid="footer-copyright">© {new Date().getFullYear()} Roze di Napoli. {t("rights")}</p>
          <a href="/admin" className="rz-link inline-flex items-center min-h-[44px]">{t("admin")}</a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
